import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BookOpenIcon, XMarkIcon } from '@heroicons/react/24/outline'

interface CourseForm {
  title: string
  description: string
  subject: string
  expectedPricePerHour: number
  duration: number
}

const EditCourse = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(false)
  const [fetchingCourse, setFetchingCourse] = useState(true)
  const [formData, setFormData] = useState<CourseForm>({
    title: '',
    description: '',
    subject: '',
    expectedPricePerHour: 0,
    duration: 1.0
  })

  const subjects = [
    '数学', '物理', '化学', '英语', '语文', '历史', '地理', '生物', 
    '编程', '计算机', '音乐', '美术', '体育', '其他'
  ]

  useEffect(() => {
    if (id) {
      fetchCourseDetail()
    }
  }, [id])

  const fetchCourseDetail = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/courses/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const course = await response.json()
        setFormData({
          title: course.title,
          description: course.description,
          subject: course.subject,
          expectedPricePerHour: course.expectedPricePerHour,
          duration: course.duration
        })
      } else {
        alert('获取课程信息失败')
        navigate('/teacher/courses')
      }
    } catch (error) {
      console.error('获取课程详情失败:', error)
      alert('获取课程信息失败')
      navigate('/teacher/courses')
    } finally {
      setFetchingCourse(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/courses/teacher/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        const result = await response.json()
        alert('课程更新成功，重新提交审核！')
        navigate('/teacher/courses')
      } else {
        const error = await response.json()
        alert(error.message || '更新失败，请重试')
      }
    } catch (error) {
      console.error('更新课程失败:', error)
      alert('更新失败，请检查网络连接')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'expectedPricePerHour' || name === 'duration' ? parseFloat(value) || 0 : value
    }))
  }

  if (fetchingCourse) {
    return (
      <div className="max-w-4xl mx-auto py-6 px-4">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold text-gray-900">编辑课程</h1>
          <p className="mt-2 text-sm text-gray-700">
            修改课程信息，提交后重新进入审核流程
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => navigate('/teacher/courses')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <XMarkIcon className="-ml-1 mr-2 h-5 w-5" />
            取消
          </button>
        </div>
      </div>

      <div className="mt-8">
        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-6 p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  课程标题 *
                </label>
                <input
                  type="text"
                  name="title"
                  id="title"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="请输入课程标题"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                  科目 *
                </label>
                <select
                  name="subject"
                  id="subject"
                  required
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">请选择科目</option>
                  {subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
                  课程时长（小时）*
                </label>
                <select
                  name="duration"
                  id="duration"
                  required
                  value={formData.duration}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value={0.5}>0.5小时</option>
                  <option value={1}>1小时</option>
                  <option value={1.5}>1.5小时</option>
                  <option value={2}>2小时</option>
                  <option value={2.5}>2.5小时</option>
                  <option value={3}>3小时</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="expectedPricePerHour" className="block text-sm font-medium text-gray-700">
                  期望每小时价格（元）*
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">¥</span>
                  </div>
                  <input
                    type="number"
                    name="expectedPricePerHour"
                    id="expectedPricePerHour"
                    required
                    min="1"
                    max="1000"
                    value={formData.expectedPricePerHour || ''}
                    onChange={handleInputChange}
                    className="block w-full pl-8 pr-12 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="100"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">/小时</span>
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  最终价格将包含20%的平台服务费
                </p>
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  课程描述 *
                </label>
                <textarea
                  name="description"
                  id="description"
                  required
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="请详细描述课程内容、适合对象、学习目标等..."
                />
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      注意事项
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <ul className="list-disc list-inside space-y-1">
                        <li>已审核通过的课程无法编辑</li>
                        <li>课程修改后将重新进入审核流程</li>
                        <li>审核期间课程状态为"审核中"</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/teacher/courses')}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      更新中...
                    </>
                  ) : (
                    <>
                      <BookOpenIcon className="-ml-1 mr-2 h-5 w-5" />
                      更新课程
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditCourse 